import { IKimAttachment } from '@alberta/konexi-shared';
import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { AlertController, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { InfoBoxType } from 'src/app/shared/components/common/info-box/info-box.component';
import { IKimRecipient, IKimSendResult } from 'src/app/shared/models/kim/kim.model';
import { SelectionItem } from 'src/app/shared/models/selectionItem';
import { KimApiService } from 'src/app/shared/services/kim/kim-api.service';
import { KimSenderAddressService } from 'src/app/shared/services/kim/kim-sender-address.service';
import { NetworkInfoService } from 'src/app/shared/services/network/network-info.service';

import { IKimRecipientSearchResult, KimRecipientSearchModal } from '../kim-recipient-search/kim-recipient-search.modal';

@Component({
  selector: 'itl-kim-send',
  templateUrl: './kim-send.modal.html',
  styleUrls: ['./kim-send.modal.scss'],
  standalone: false,
})
export class KimSendModal implements OnInit {
  @Input() attachment: IKimAttachment;
  @Input() subject: string;
  @Input() body: string;

  public senderControl = new UntypedFormControl(null, Validators.required);
  public recipientControl = new UntypedFormControl(null, Validators.required);
  public senderSelectionItems: SelectionItem[] = [];
  public noKimAddress = false;
  public vzdUnavailable = false;
  public sending = false;
  public infoBoxTypeWarning = InfoBoxType.Warning;

  public get recipient(): IKimRecipient | null {
    return this.recipientControl.value;
  }

  public get pageIsValid(): boolean {
    return !this.noKimAddress && !this.sending && this.senderControl.valid && Boolean(this.recipient);
  }

  constructor(
    private _modalController: ModalController,
    private _alertController: AlertController,
    private _translateService: TranslateService,
    private _kimSenderAddressService: KimSenderAddressService,
    private _kimApiService: KimApiService,
    private _networkInfoService: NetworkInfoService
  ) {}

  async ngOnInit(): Promise<void> {
    this.senderSelectionItems = await this._kimSenderAddressService.getSenderSelectionItems();

    if (!this.senderSelectionItems.length) {
      this.noKimAddress = true;
      return;
    }

    if (this.senderSelectionItems.length === 1) {
      this.senderControl.setValue(this.senderSelectionItems[0].id);
    }
  }

  public async openRecipientSearch(): Promise<void> {
    if (this.isOffline) {
      // Opening a search that cannot reach the directory only hides the info box behind an
      // empty wizard, so state the problem instead.
      this.vzdUnavailable = true;
      return;
    }

    const modal = await this._modalController.create({
      component: KimRecipientSearchModal,
      backdropDismiss: false,
      cssClass: 'fullPageModal',
    });
    await modal.present();
    const result = await modal.onDidDismiss();
    const data = result.data as IKimRecipientSearchResult | undefined;
    if (data?.recipient) {
      this.recipientControl.setValue(data.recipient);
      this.vzdUnavailable = false;
      return;
    }
    if (data?.vzdUnavailable === true) {
      // An already selected recipient stays visible - the info box would replace the input.
      this.vzdUnavailable = !this.recipient;
      return;
    }
    if (data?.vzdUnavailable === false) {
      this.vzdUnavailable = false;
    }
  }

  public onSenderChange(value: string | null | undefined): void {
    if (!value) {
      // Clearing is a deliberate action, so the field may show that it is mandatory.
      this.senderControl.markAsTouched();
    }
  }

  public removeRecipient(event: Event): void {
    event.stopPropagation();
    this.recipientControl.setValue(null);
    this.recipientControl.markAsTouched();
  }

  public async retryVzd(): Promise<void> {
    // The info box is kept until a search actually reaches the directory again.
    await this.openRecipientSearch();
  }

  public async cancel(): Promise<void> {
    await this._modalController.dismiss().catch(error => console.log(error));
  }

  public async send(): Promise<void> {
    if (!this.pageIsValid) {
      this.senderControl.markAsTouched();
      this.recipientControl.markAsTouched();
      return;
    }

    if (this.isOffline) {
      await this.showRetryAlert('kim.send.offline.header', 'kim.send.offline.message');
      return;
    }

    this.sending = true;
    try {
      const sender: string = this.senderControl.value;
      await this._kimApiService.send({
        from: sender,
        to: this.recipient.kimAddress,
        subject: this.subject,
        body: this.body ?? this._translateService.instant('kim.send.defaultBody'),
        attachments: this.attachment ? [this.attachment] : undefined,
      });
      const sendResult: IKimSendResult = {
        sent: true,
        sender,
        recipient: this.recipient,
        sentAt: new Date(),
      };
      await this._modalController.dismiss(sendResult);
    } catch (error) {
      window.logger.error('KIM send failed', error);
      this.sending = false;
      await this.showRetryAlert('kim.send.error.header', 'kim.send.error.message');
    } finally {
      this.sending = false;
    }
  }

  private get isOffline(): boolean {
    return this._networkInfoService.isNotConnected || navigator.onLine === false;
  }

  private async showRetryAlert(headerKey: string, messageKey: string): Promise<void> {
    const alert = await this._alertController.create({
      header: this._translateService.instant(headerKey),
      message: this._translateService.instant(messageKey),
      buttons: [
        { text: this._translateService.instant('kim.send.close'), role: 'cancel' },
        { text: this._translateService.instant('kim.send.retry'), role: 'retry' },
      ],
      backdropDismiss: false,
    });
    await alert.present();
    const result = await alert.onDidDismiss();
    if (result.role === 'retry') {
      await this.send();
    }
  }
}
